import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { hrefFor } from '../lib/router.js';
import { BrandSelect } from '../lib/brands.js';
import type { Rights } from '../lib/session.js';
import {
  CHANNEL_LABEL,
  OUTCOME_LABEL,
  REASON_LABEL,
  SENDER_LABEL,
  WINDOW_LABEL,
  label,
} from '../lib/labels.js';
import { date, num } from '../lib/format.js';
import { TemplateEditor } from './TemplateEditor.js';
import { TemplateGallery } from './TemplateGallery.js';
import { Flow } from './Flow.js';
import { FlowMap } from './FlowMap.js';
import { stripFocus } from '../components/flowmap/model.js';
import { Channels } from './Channels.js';
import { Segments } from './Segments.js';
import { Broadcasts } from './Broadcasts.js';
import { BroadcastPlan } from './BroadcastPlan.js';
import { SentTab } from './SentTab.js';
import { Sending } from './Sending.js';
import { SendTests } from './SendTests.js';
import { Watchdogs } from './Watchdogs.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Loading,
  PageHeader,
  Panel,
  TabBar,
  Toggle,
} from '../components/ui.js';
import type { IconName } from '../components/ui.js';
import { Badge as Pill } from '../components/2b-status.js';
import '../screens-2b.css';

interface CatalogueItem {
  code: string;
  names: string[];
  channel: string;
  sender: string;
  scope: string;
  window: string;
  pacingExempt: boolean;
  mergeGroup: string | null;
  sendingAllowed: boolean;
  switchReason: string | null;
  last30: {
    due: number;
    sent: number;
    merged: number;
    held: number;
    deferred: number;
    suppressed: number;
    expired: number;
    delivered: number;
    clicked: number;
    unsubscribed: number;
    bounced: number;
  };
  reasons: Array<{ outcome: string; reason: string; count: number }>;
}

interface TemplateItem {
  templateKey: string;
  providerTemplateId: string | null;
  providerListId: string | null;
  missing: boolean;
}

type Tab =
  | 'sending'
  | 'catalogue'
  | 'sent'
  | 'flow'
  | 'map'
  | 'segments'
  | 'broadcastplan'
  | 'broadcasts'
  | 'plan'
  | 'watchdogs'
  | 'editor'
  | 'channels'
  | 'templates'
  | 'gallery'
  | 'sendtests';

const TABS: Array<{ id: Tab; label: string; hint: string; icon: IconName }> = [
  {
    id: 'sending',
    label: 'Odesílání',
    hint: 'Co se odeslalo, co se pošle a co se chystá',
    icon: 'send',
  },
  { id: 'catalogue', label: 'Přehled zpráv', hint: 'Vypínače a čísla za 30 dní', icon: 'mail' },
  { id: 'sent', label: 'Odeslané', hint: 'Kniha komunikace: servis a marketing', icon: 'send' },
  { id: 'flow', label: 'Tok zpráv', hint: 'Kdy se která zpráva vyhodnotí', icon: 'flow' },
  { id: 'map', label: 'Schéma toku', hint: 'Kudy data tečou a kde se zastaví', icon: 'route' },
  { id: 'channels', label: 'Kanály', hint: 'Jak zpráva odchází a kde je šablona', icon: 'plug' },
  { id: 'editor', label: 'Šablony', hint: 'Obsah e-mailů a mapování v SE', icon: 'file' },
  { id: 'segments', label: 'Segmenty', hint: 'Skupiny podle chování a zájmu', icon: 'segment' },
  {
    id: 'broadcastplan',
    label: 'Plán rozesílek',
    hint: 'Týdenní plán mailingu a koncepty z něj',
    icon: 'calendar',
  },
  {
    id: 'broadcasts',
    label: 'Rozesílky',
    hint: 'Jednorázové odeslání šablony na seznam',
    icon: 'send',
  },
  {
    id: 'watchdogs',
    label: 'Hlídací psi',
    hint: 'Poptávka a nákupy po výzvě',
    icon: 'bolt',
  },
  { id: 'plan', label: 'Plán odesílání', hint: 'Co konkrétně odejde za 7 dní', icon: 'calendar' },
  {
    id: 'sendtests',
    label: 'Test odesílání',
    hint: 'Šablony a tok na testovací adresu',
    icon: 'flask',
  },
];

/** Cesta jedne zpravy hubem; kazdy krok vede na zalozku, kde se dá nastavit. */
const STEPS: Array<{ label: string; tip: string }> = [
  {
    label: 'Událost',
    tip: 'Objednávka nebo změna závodu z e-shopu spustí vyhodnocení.',
  },
  { label: 'Plán', tip: 'Hub spočítá, která zpráva a kdy má zákazníkovi odejít.' },
  {
    label: 'Rozhodnutí',
    tip: 'Každá zpráva se pošle, sloučí, podrží nebo potlačí — vždy se záznamem důvodu.',
  },
  { label: 'Šablona', tip: 'Obsah e-mailu pro kód zprávy, prodejce a jazyk.' },
  {
    label: 'Odeslání',
    tip: 'Servis jde transakčně z hubu, marketing přes SmartEmailing.',
  },
  {
    label: 'Reakce',
    tip: 'Doručeno, kliknuto, odhlášeno a odskočeno u každé zprávy.',
  },
];

export function Communication({ rights }: { rights: Rights }): JSX.Element {
  const [tab, setTab] = useState<Tab>('sending');
  const [openBroadcast, setOpenBroadcast] = useState<number | null>(null);
  const [templateKey, setTemplateKey] = useState<string | undefined>();
  const [focus, setFocus] = useState<{ ids: string[]; seq: number }>();
  const group: Tab = tab === 'templates' || tab === 'gallery' ? 'editor' : tab;

  return (
    <>
      <PageHeader
        title="Komunikace"
        description="Odsud se řídí, co a kdy zákazníkům odchází: vypínače zpráv, tok zpráv, šablony e-mailů a zkušební odeslání."
      />

      <div className="howflow" aria-label="Jak to celé funguje">
        <span className="howflow-title">Jak to celé funguje</span>
        <ol>
          {STEPS.map((st, i) => (
            <li key={st.label}>
              <button
                className={`stepchip${group === 'map' && focus?.ids.join() === stripFocus[st.label]?.join() ? ' on' : ''}`}
                title={st.tip}
                onClick={() => {
                  setFocus({ ids: stripFocus[st.label] ?? [], seq: Date.now() });
                  setTab('map');
                }}
              >
                <i>{i + 1}</i>
                {st.label}
              </button>
              {i < STEPS.length - 1 ? <Icon name="chevron" size={14} className="sep" /> : null}
            </li>
          ))}
        </ol>
      </div>

      <TabBar
        tabs={TABS.filter((t) => t.id !== 'sendtests' || rights.can('run_send_tests'))}
        value={group}
        onChange={(t) => {
          setTab(t);
          setOpenBroadcast(null);
          setTemplateKey(undefined);
        }}
      />

      {group === 'editor' ? (
        <div className="seg" role="tablist" aria-label="Šablony">
          <button
            role="tab"
            aria-selected={tab === 'editor'}
            className={tab === 'editor' ? 'on' : ''}
            onClick={() => setTab('editor')}
          >
            Editor šablon
          </button>
          <button
            role="tab"
            aria-selected={tab === 'templates'}
            className={tab === 'templates' ? 'on' : ''}
            onClick={() => setTab('templates')}
          >
            Mapování v SmartEmailingu
          </button>
          <button
            role="tab"
            aria-selected={tab === 'gallery'}
            className={tab === 'gallery' ? 'on' : ''}
            onClick={() => setTab('gallery')}
          >
            Galerie sad
          </button>
        </div>
      ) : null}

      {tab === 'sending' ? (
        <Sending
          links={{
            onOpenTemplate: (key) => {
              setTemplateKey(key);
              setTab('editor');
            },
            onOpenBroadcast: (id) => {
              setOpenBroadcast(id);
              setTab('broadcasts');
            },
            onOpenPlan: () => setTab('broadcastplan'),
          }}
        />
      ) : null}
      {tab === 'catalogue' ? <CatalogueTab rights={rights} /> : null}
      {tab === 'sent' ? <SentTab /> : null}
      {tab === 'flow' ? <Flow rights={rights} /> : null}
      {tab === 'map' ? <FlowMap onNavigate={setTab} {...(focus ? { focus } : {})} /> : null}
      {tab === 'segments' ? <Segments rights={rights} /> : null}
      {tab === 'broadcastplan' ? (
        <BroadcastPlan
          rights={rights}
          onOpenBroadcast={(id) => {
            setOpenBroadcast(id);
            setTab('broadcasts');
          }}
        />
      ) : null}
      {tab === 'broadcasts' ? <Broadcasts rights={rights} initialOpenId={openBroadcast} /> : null}
      {tab === 'plan' ? <PlanTab /> : null}
      {tab === 'watchdogs' ? <Watchdogs rights={rights} /> : null}
      {tab === 'editor' ? (
        <TemplateEditor rights={rights} {...(templateKey ? { initialKey: templateKey } : {})} />
      ) : null}
      {tab === 'channels' ? <Channels rights={rights} /> : null}
      {tab === 'sendtests' && rights.can('run_send_tests') ? (
        <SendTests onOpenSent={() => setTab('sent')} />
      ) : null}
      {tab === 'templates' ? <TemplatesTab rights={rights} /> : null}
      {tab === 'gallery' ? <TemplateGallery /> : null}
    </>
  );
}

function CatalogueTab({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [brand, setBrand] = useState('');
  const [askGlobal, setAskGlobal] = useState(false);

  const query = useQuery<{ globalSendingAllowed: boolean; items: CatalogueItem[] }>({
    queryKey: ['catalogue', brand],
    queryFn: () => api.get(`/api/communication/catalogue${qs({ brand })}`),
  });

  const toggle = useMutation({
    mutationFn: (input: { code: string; sendingAllowed: boolean }) =>
      api.patch(`/api/communication/switches/${encodeURIComponent(input.code)}`, {
        sendingAllowed: input.sendingAllowed,
      }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['catalogue'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <Loading what="katalog" />;
  if (query.isError)
    return (
      <ErrorState
        title="Přehled zpráv se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const data = query.data;
  if (!data) return <Loading />;

  const canToggle = rights.can('toggle_send_switch');

  return (
    <>
      <PageHeader
        level={2}
        title="Přehled zpráv"
        description="Vypínače po jednotlivých zprávách a čísla za posledních 30 dní. Při zavádění jděte v pořadí: nejdřív šablony, pak tok zpráv, nakonec vypínače. Dokud je hlavní vypínač vypnutý, neodejde nic."
      />
      {error ? <ErrorState title="Vypínač se nepodařilo změnit" error={new Error(error)} /> : null}

      <Panel title="Hlavní vypínač">
        <div className="row">
          {data.globalSendingAllowed ? (
            <Pill tone="warn">zapnutý — zprávy mohou odcházet</Pill>
          ) : (
            <Pill tone="ok">vypnutý — nic neodejde</Pill>
          )}
          {canToggle ? (
            <button
              type="button"
              className={`btn${data.globalSendingAllowed ? ' dng' : ''}`}
              onClick={() => setAskGlobal(true)}
            >
              {data.globalSendingAllowed ? 'Zastavit všechno' : 'Povolit odesílání'}
            </button>
          ) : null}
        </div>
      </Panel>
      <ConfirmDialog
        open={askGlobal}
        title={data.globalSendingAllowed ? 'Zastavit odesílání všech zpráv?' : 'Povolit odesílání?'}
        danger={data.globalSendingAllowed}
        confirmLabel={data.globalSendingAllowed ? 'Zastavit všechno' : 'Povolit odesílání'}
        busy={toggle.isPending}
        onCancel={() => setAskGlobal(false)}
        onConfirm={() => {
          setAskGlobal(false);
          toggle.mutate({ code: '*', sendingAllowed: !data.globalSendingAllowed });
        }}
      >
        <p>
          {data.globalSendingAllowed
            ? 'Vypnutím hlavního vypínače se zastaví všechny zprávy, dokud ho znovu nezapnete.'
            : 'Zapnutím povolíte odesílání u zpráv, které mají vlastní vypínač zapnutý. Jestli zprávy opravdu odejdou zákazníkům, určuje navíc režim odesílání nahoře.'}
        </p>
      </ConfirmDialog>

      <Panel
        title="Katalog zpráv (posledních 30 dní)"
        action={<BrandSelect value={brand} onChange={setBrand} />}
      >
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Kód</th>
                <th>Název</th>
                <th>Kanál</th>
                <th>Posílá</th>
                <th>Smí odejít</th>
                <th className="n">Na řadě</th>
                <th className="n">Odesláno</th>
                <th className="n">Sloučeno</th>
                <th className="n">Drženo</th>
                <th className="n">Odloženo</th>
                <th className="n">Potlačeno</th>
                <th className="n">Propadlo</th>
                <th className="n">Doručeno</th>
                <th className="n">Prokliky</th>
                <th className="n">Odhlášení</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.code}>
                  <td>
                    <button
                      className="click"
                      onClick={() => setOpen(open === item.code ? null : item.code)}
                    >
                      <code>{item.code}</code>
                    </button>
                  </td>
                  <td>
                    {item.names[0]}
                    {item.names.length > 1 ? (
                      <div className="muted">{item.names.slice(1).join(' · ')}</div>
                    ) : null}
                    <div className="muted">
                      {label(WINDOW_LABEL, item.window)}
                      {item.mergeGroup
                        ? ` · slučuje se s dalšími (skupina ${item.mergeGroup})`
                        : ''}
                      {item.pacingExempt ? ' · mimo frekvenční limit' : ''}
                    </div>
                  </td>
                  <td>{label(CHANNEL_LABEL, item.channel)}</td>
                  <td>{label(SENDER_LABEL, item.sender)}</td>
                  <td>
                    {item.sender === 'eshop' ? (
                      <span className="muted">posílá e-shop</span>
                    ) : (
                      <Toggle
                        checked={item.sendingAllowed}
                        disabled={!canToggle}
                        label={`Smí odejít ${item.code}`}
                        onChange={(value) =>
                          toggle.mutate({ code: item.code, sendingAllowed: value })
                        }
                      />
                    )}
                  </td>
                  <td className="n">{num(item.last30.due)}</td>
                  <td className="n">{num(item.last30.sent)}</td>
                  <td className="n">{num(item.last30.merged)}</td>
                  <td className="n">{num(item.last30.held)}</td>
                  <td className="n">{num(item.last30.deferred)}</td>
                  <td className="n">{num(item.last30.suppressed)}</td>
                  <td className="n">{num(item.last30.expired)}</td>
                  <td className="n">{num(item.last30.delivered)}</td>
                  <td className="n">{num(item.last30.clicked)}</td>
                  <td className="n">{num(item.last30.unsubscribed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {open ? (
          <div className="panel s2b-gap">
            <h3>Rozpad důvodů — {open}</h3>
            {(() => {
              const item = data.items.find((i) => i.code === open);
              if (!item || item.reasons.length === 0) {
                return <p className="note">Za 30 dní u tohoto kódu nic neodpadlo.</p>;
              }
              return (
                <table>
                  <thead>
                    <tr>
                      <th>Výsledek</th>
                      <th>Důvod</th>
                      <th className="n">Počet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.reasons.map((r, i) => (
                      <tr key={i}>
                        <td>{label(OUTCOME_LABEL, r.outcome)}</td>
                        <td>{label(REASON_LABEL, r.reason)}</td>
                        <td className="n">{num(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        ) : null}
      </Panel>
    </>
  );
}

function PlanTab(): JSX.Element {
  const [days, setDays] = useState(7);
  const query = useQuery<{
    from: string;
    to: string;
    items: Array<{
      code: string;
      day: string;
      race_id: string | null;
      race_name: string | null;
      n: number;
      merge_only: number;
      held: number;
      deferred: number;
    }>;
  }>({
    queryKey: ['plan', days],
    queryFn: () => api.get(`/api/communication/plan${qs({ days })}`),
  });

  if (query.isLoading) return <Loading what="plán" />;
  if (query.isError)
    return (
      <ErrorState
        title="Plán se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );

  const items = query.data?.items ?? [];
  const byDay = new Map<string, typeof items>();
  for (const item of items) {
    const list = byDay.get(item.day) ?? [];
    list.push(item);
    byDay.set(item.day, list);
  }

  return (
    <>
      <PageHeader
        level={2}
        title="Plán odesílání"
        description="Co konkrétně je na řadě v nejbližších dnech. Číslo ještě neznamená odeslání: každá zpráva projde rozhodováním (sloučení, podržení, odložení)."
      />
      <Panel
        title={`Plán na ${days} dní`}
        action={
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Délka plánu"
          >
            <option value={7}>7 dní</option>
            <option value={14}>14 dní</option>
            <option value={30}>30 dní</option>
          </select>
        }
      >
        {items.length === 0 ? (
          <EmptyState title="Nic na řadě" icon="calendar">
            V příštích {days} dnech není naplánovaná žádná zpráva.
          </EmptyState>
        ) : (
          [...byDay.entries()].map(([day, rows]) => (
            <div key={day} className="s2b-day">
              <h3>{date(day)}</h3>
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Kód</th>
                      <th>Závod</th>
                      <th className="n">Počet</th>
                      <th className="n">Jen ke sloučení</th>
                      <th className="n">Drženo</th>
                      <th className="n">Odloženo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td>
                          <code>{r.code}</code>
                        </td>
                        <td>
                          {r.race_id ? (
                            <a href={hrefFor('races', r.race_id)}>{r.race_name ?? r.race_id}</a>
                          ) : (
                            <span className="muted">bez závodu</span>
                          )}
                        </td>
                        <td className="n">{num(r.n)}</td>
                        <td className="n">{num(r.merge_only)}</td>
                        <td className="n">{num(r.held)}</td>
                        <td className="n">{num(r.deferred)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </Panel>
    </>
  );
}

function TemplatesTab({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery<{ items: TemplateItem[]; missingCount: number }>({
    queryKey: ['templates'],
    queryFn: () => api.get('/api/template-mappings'),
  });
  const save = useMutation({
    mutationFn: (input: {
      templateKey: string;
      providerTemplateId: string;
      providerListId: string;
    }) =>
      api.put(`/api/template-mappings/${encodeURIComponent(input.templateKey)}`, {
        providerTemplateId: input.providerTemplateId || null,
        providerListId: input.providerListId || null,
      }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <Loading what="šablony" />;
  if (query.isError)
    return (
      <ErrorState
        title="Mapování se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const items = (query.data?.items ?? []).filter((i) => !onlyMissing || i.missing);
  const canMap = rights.can('map_templates');

  return (
    <>
      {error ? <ErrorState title="Mapování se nepodařilo uložit" error={new Error(error)} /> : null}
      <PageHeader
        level={2}
        title="Mapování ve SmartEmailingu"
        description="Propojení šablon hubu s e-maily a seznamy ve SmartEmailingu. Zpráva s nenamapovanou šablonou se podrží a neodejde."
      />
      <Panel
        title="Mapování šablon na SmartEmailing"
        action={
          <label className="row">
            <input
              type="checkbox"
              checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)}
            />
            <span className="note">jen chybějící</span>
          </label>
        }
      >
        {(query.data?.missingCount ?? 0) > 0 ? (
          <p className="hint">
            Chybí <b>{num(query.data?.missingCount)}</b> mapování. Zpráva s nenamapovanou šablonou
            se <b>drží</b> a neodejde.
          </p>
        ) : (
          <p className="note">Všechny šablony jsou namapované.</p>
        )}
        {items.length === 0 ? (
          <EmptyState title="Nic nechybí">
            Všechny šablony jsou namapované. Odškrtněte „jen chybějící“, pokud chcete vidět všechny.
          </EmptyState>
        ) : null}
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Klíč šablony</th>
                <th>ID šablony ve SmartEmailingu</th>
                <th>ID seznamu ve SmartEmailingu</th>
                <th>Stav</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 400).map((item) => (
                <TemplateRow
                  key={item.templateKey}
                  item={item}
                  canMap={canMap}
                  onSave={(templateId, listId) =>
                    save.mutate({
                      templateKey: item.templateKey,
                      providerTemplateId: templateId,
                      providerListId: listId,
                    })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

function TemplateRow({
  item,
  canMap,
  onSave,
}: {
  item: TemplateItem;
  canMap: boolean;
  onSave: (templateId: string, listId: string) => void;
}): JSX.Element {
  const [templateId, setTemplateId] = useState(item.providerTemplateId ?? '');
  const [listId, setListId] = useState(item.providerListId ?? '');
  const dirty =
    templateId !== (item.providerTemplateId ?? '') || listId !== (item.providerListId ?? '');

  return (
    <tr>
      <td>
        <code>{item.templateKey}</code>
      </td>
      <td>
        <input
          type="text"
          value={templateId}
          disabled={!canMap}
          aria-label={`ID šablony pro ${item.templateKey}`}
          onChange={(e) => setTemplateId(e.target.value)}
        />
      </td>
      <td>
        <input
          type="text"
          value={listId}
          disabled={!canMap}
          aria-label={`ID seznamu pro ${item.templateKey}`}
          onChange={(e) => setListId(e.target.value)}
        />
      </td>
      <td>
        {dirty && canMap ? (
          <button
            className="btn pri"
            aria-label={`Uložit mapování ${item.templateKey}`}
            onClick={() => onSave(templateId, listId)}
          >
            Uložit
          </button>
        ) : item.missing ? (
          <Pill tone="bad">chybí</Pill>
        ) : (
          <Pill tone="ok">namapováno</Pill>
        )}
      </td>
    </tr>
  );
}
